import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useNavigationType } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { LogoImage } from '@/components/ui/LogoImage';
import { MatchCard } from '@/components/match/MatchCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { ObservationCard } from '@/components/observation/ObservationCard';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services/api';
import { useMatchStore, getWeekendBounds, matchHasKnownKickoffTime } from '@/stores/matchStore';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { useObservationStore } from '@/stores/observationStore';
import {
  buildMatchLookupMap,
  enrichObservationWithMatchMap,
} from '@/utils/enrichObservationWithMatch';
import { isObservationVisibleToViewer } from '@/utils/observationDisplay';
import type { Club, Match, Observation } from '@/types';
import { useProfileStore } from '@/stores/profileStore';
import { trackUsageEvent } from '@/services/usageEvent';
import { clubConnexionCle } from '@/utils/favoriteKeys';

function matchesClub(match: Match, clubNom: string): boolean {
  if (!clubNom) return false;
  const q = clubNom.toLowerCase().trim();
  const home = (match.homeTeam || '').toLowerCase().trim();
  const away = (match.awayTeam || '').toLowerCase().trim();
  return home === q || away === q || home.includes(q) || away.includes(q);
}

const TABS = [
  { id: 'matches', label: 'Match du WE' },
  { id: 'observations', label: 'Observations' },
] as const;

type ClubTabId = (typeof TABS)[number]['id'];

export default function ClubDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const colors = useTheme();
  const [club, setClub] = useState<Club | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<ClubTabId>('matches');
  const { matches, fetchMatches } = useMatchStore();
  const { load: loadFavorites, isClubFavorite, toggleClub } = useFavoritesStore();
  const { all: allObservations, fetchAll: fetchAllObservations, getByClubName } = useObservationStore();
  const tifosiCle = useProfileStore((state) => state.tifosiCle);
  const navigationType = useNavigationType();

  useEffect(() => { loadClub(); }, [id]);
  useEffect(() => { fetchMatches(); }, []);
  useEffect(() => { loadFavorites(); }, [loadFavorites]);
  useEffect(() => { if (allObservations.length === 0) fetchAllObservations(); }, [allObservations.length, fetchAllObservations]);

  /** GoPaaS `tifosi_event` (club_opened) — profil connecté uniquement ; pas sur retour historique (`POP`) ; déduplication courte (Strict Mode). */
  useEffect(() => {
    if (navigationType === 'POP') return;
    if (!club || !tifosiCle) return;
    const clubKey = String(club.idclub || id || '').trim();
    if (!clubKey) return;
    trackUsageEvent(
      { type: 'club_opened', tifosi: tifosiCle, club: clubKey },
      { navigationType, dedupeContext: clubKey },
    );
  }, [club, tifosiCle, id, navigationType]);

  const loadClub = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const result = await apiService.getClubById(id);
      if (result?.data) setClub(result.data);
    } catch { setClub(null); }
    finally { setIsLoading(false); }
  };

  const handleRefresh = async () => {
    if (activeTab === 'matches') {
      await Promise.all([loadClub(), fetchMatches(true)]);
    } else {
      await fetchAllObservations(true);
    }
  };

  const { start, end } = getWeekendBounds(0);
  const weekendMatches = matches.filter((m) => {
    if (!m.date || !club) return false;
    const d = new Date(m.date);
    if (d < start || d > end) return false;
    return matchesClub(m, club.nom) && matchHasKnownKickoffTime(m);
  });

  const matchById = useMemo(() => buildMatchLookupMap(matches), [matches]);
  const clubObservations = useMemo(() => {
    const list = club ? getByClubName(club.nom) : [];
    return list
      .filter((obs) => isObservationVisibleToViewer(obs, tifosiCle))
      .map((obs) => enrichObservationWithMatchMap(obs, matchById));
  }, [club, allObservations, getByClubName, matchById, tifosiCle]);
  const openObservation = (observation: Observation) => {
    navigate('/observation/detailed', {
      state: {
        mode: 'edit',
        observation,
      },
    });
  };
  const clubFavoriteCle = clubConnexionCle(club);
  const isFavorite = clubFavoriteCle
    ? isClubFavorite(clubFavoriteCle)
    : id
      ? isClubFavorite(id)
      : false;
  const handleToggleFavorite = () => {
    if (!clubFavoriteCle) return;
    toggleClub(clubFavoriteCle);
  };

  const openDirections = () => {
    if (!club) return;
    const firstWithCoords = weekendMatches.find((m) => m.latitude && m.longitude);
    if (firstWithCoords?.latitude && firstWithCoords?.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${firstWithCoords.latitude},${firstWithCoords.longitude}`, '_blank');
    } else if (club.ville) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(club.ville + ' France')}`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(club.nom + ' France')}`, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
        <Header navigate={navigate} colors={colors} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner" /></div>
      </div>
    );
  }

  if (!club) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
        <Header navigate={navigate} colors={colors} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><EmptyState icon="shield" title="Club non trouvé" /></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: colors.background }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 8px', borderBottom: `1px solid ${colors.border}` }}>
        <button onClick={() => navigate(-1)} style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <Icon name="arrow-back" size={24} color={colors.text} />
        </button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, minWidth: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background }}>
            <LogoImage uri={club.logo} size={32} />
          </div>
          <span style={{ flex: 1, fontSize: 18, fontWeight: 700, color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{club.nom}</span>
        </div>
        <button
          onClick={handleToggleFavorite}
          aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}
        >
          <Icon name={isFavorite ? 'star' : 'star-outline'} size={24} color={isFavorite ? Colors.primary : colors.text} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16, paddingBottom: 40 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 24, borderRadius: 16, border: `1px solid ${colors.border}`, backgroundColor: colors.surface, marginBottom: 24 }}>
          <div style={{ width: 96, height: 96, borderRadius: 24, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background, marginBottom: 16 }}>
            <LogoImage uri={club.logo} size={72} />
          </div>
          <span style={{ fontSize: 22, fontWeight: 800, textAlign: 'center', marginBottom: 8, color: colors.text }}>{club.nom}</span>
          {club.ville && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Icon name="location-outline" size={16} color={colors.textSecondary} />
              <span style={{ fontSize: 15, color: colors.textSecondary }}>{club.ville}</span>
            </div>
          )}
          {club.competition && <span style={{ fontSize: 13, fontWeight: 600, marginBottom: 20, color: Colors.primary }}>{club.competition}</span>}
          <button onClick={openDirections} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 14, paddingBottom: 14, paddingLeft: 24, paddingRight: 24, borderRadius: 24, backgroundColor: Colors.primary, cursor: 'pointer' }}>
            <Icon name="navigate" size={20} color="#ffffff" />
            <span style={{ color: '#ffffff', fontSize: 16, fontWeight: 700 }}>Itinéraire</span>
          </button>
        </div>

        <div style={{ display: 'flex', borderBottom: `1px solid ${Colors.primary}15`, marginBottom: 16 }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const count = tab.id === 'matches' ? weekendMatches.length : clubObservations.length;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '12px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  borderBottom: `2px solid ${isActive ? Colors.primary : 'transparent'}`,
                  background: 'transparent',
                  border: 'none',
                  borderBottomWidth: 2,
                  borderBottomStyle: 'solid',
                  borderBottomColor: isActive ? Colors.primary : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: isActive ? Colors.primary : colors.textSecondary }}>
                  {tab.label}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    minWidth: 22,
                    height: 20,
                    padding: '0 6px',
                    borderRadius: 10,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: isActive ? Colors.primary : `${Colors.primary}20`,
                    color: isActive ? '#ffffff' : Colors.primary,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            onClick={handleRefresh}
            aria-label="Actualiser"
            style={{ width: 36, height: 36, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: colors.surface, border: `1px solid ${colors.border}`, cursor: 'pointer' }}
          >
            <Icon name="refresh" size={18} color={colors.text} />
          </button>
        </div>

        {activeTab === 'matches' ? (
          weekendMatches.length === 0 ? (
            <EmptyState icon="football" title="Aucun match ce week-end" subtitle={`Pas de match programmé pour ${club.nom}`} />
          ) : (
            weekendMatches.map((match) => <MatchCard key={match.idmatch_football} match={match} />)
          )
        ) : clubObservations.length === 0 ? (
          <div style={{ padding: 24, borderRadius: 16, border: `1px dashed ${colors.border}`, textAlign: 'center', backgroundColor: colors.surface }}>
            <Icon name="analytics-outline" size={32} color={colors.textSecondary} />
            <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 8 }}>
              Aucune observation pour ce club
            </div>
          </div>
        ) : (
          clubObservations.map((obs) => (
            <ObservationCard key={obs.id} observation={obs} onPress={() => openObservation(obs)} />
          ))
        )}
      </div>
    </div>
  );
}

function Header({ navigate, colors }: { navigate: any; colors: any }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', padding: '12px 8px', borderBottom: `1px solid ${colors.border}` }}>
      <button onClick={() => navigate(-1)} style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <Icon name="arrow-back" size={24} color={colors.text} />
      </button>
      <span style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>Club</span>
    </div>
  );
}
